import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import './AuthStyles.css';

// Constants for storage keys to ensure consistency
export const STORAGE_KEYS = {
  TOKEN: 'token',
  TOKEN_EXPIRY: 'tokenExpiry',
  USER_INFO: 'userInfo',
  SESSION_REFRESH: 'sessionRefresh', // For cross-tab communication
  ACTIVE_SESSION: 'activeSession'     // New key to track active session
};

// Authentication Hook to handle token expiry
export const useAuthCheck = () => {
  const navigate = useNavigate();
  const [toastId, setToastId] = useState(null);

  // Function to refresh token - using useCallback to avoid unnecessary recreations
  const refreshToken = useCallback(() => {
    try {
      // Here you would typically call your API to refresh the token
      // For this example, we'll just extend the expiry by 30 minutes
      const newExpiry = Date.now() + 30 * 60 * 1000; // 30 minutes
      
      // Update token expiry
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, newExpiry.toString());
      
      // Update active session timestamp
      localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, Date.now().toString());
      
      // Set a timestamp to notify other tabs of the refresh
      localStorage.setItem(STORAGE_KEYS.SESSION_REFRESH, Date.now().toString());
      
      // Dismiss the toast if it exists
      if (toastId) {
        toast.dismiss(toastId);
        setToastId(null);
      }
      
      toast.success('Session extended successfully!', {
        position: "top-center",
        autoClose: 3000
      });
    } catch (error) {
      console.error("Error refreshing token:", error);
      handleTokenExpiry();
    }
  }, [toastId]);
  
  // Function to handle token expiry and cleanup
  const handleTokenExpiry = useCallback(() => {
    // First dismiss any existing toasts
    if (toastId) {
      toast.dismiss(toastId);
      setToastId(null);
    }
    
    // Clean up localStorage
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Show expired notification
    toast.error('Your session has expired', {
      position: "top-center",
      autoClose: 3000
    });
    
    // Use setTimeout to prevent issues with unmounting components
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 100);
  }, [navigate, toastId]);
  
  // Function to check token validity
  const checkTokenValidity = useCallback(() => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const tokenExpiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    
    if (!token || !tokenExpiry) return;
    
    const expiryTime = parseInt(tokenExpiry);
    const timeRemaining = expiryTime - Date.now();
    
    // If already expired
    if (timeRemaining <= 0) {
      handleTokenExpiry();
      return;
    } 
    
    // Update active session timestamp
    localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, Date.now().toString());
    
    // If token will expire in the next 5 minutes and no toast is currently shown
    if (timeRemaining < 5 * 60 * 1000 && !toastId) {
      // Show warning toast with refresh option
      const id = toast.warning(
        <div>
          <p>Your session will expire soon</p>
          <button 
            onClick={refreshToken}
            className="session-extend-btn"
          >
            Stay logged in
          </button>
        </div>,
        {
          position: "top-center",
          autoClose: false,
          closeOnClick: false,
          draggable: true,
          pauseOnHover: true
        }
      );
      setToastId(id);
    }
  }, [handleTokenExpiry, refreshToken, toastId]);
  
  useEffect(() => {
    // Initial check
    checkTokenValidity();
    
    // Set up interval to periodically check token validity
    const intervalId = setInterval(checkTokenValidity, 15000); // Check every 15 seconds
    
    // Listen for storage changes from other tabs
    const handleStorageChange = (event) => {
      if (event.key === STORAGE_KEYS.SESSION_REFRESH) {
        // Clear any existing expiry warning toast
        if (toastId) {
          toast.dismiss(toastId);
          setToastId(null);
        }
      }
      
      // If token was refreshed in another tab, update current tab's session
      if (event.key === STORAGE_KEYS.TOKEN_EXPIRY) {
        checkTokenValidity();
      }
      
      // If user logged out in another tab
      if (event.key === STORAGE_KEYS.TOKEN && event.newValue === null) {
        handleTokenExpiry();
      }
    };
    
    // Add event listener for localStorage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Send a ping to indicate this tab is active
    localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, Date.now().toString());
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
      
      // Clear any remaining toasts when component unmounts
      if (toastId) {
        toast.dismiss(toastId);
      }
    };
  }, [checkTokenValidity, handleTokenExpiry, toastId]);
  
  // Optional: Set up beforeunload event to update last active timestamp
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, Date.now().toString());
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  return null;
};

// Function to check if user is authenticated (can be used outside of React components)
export const isAuthenticated = () => {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  const tokenExpiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  
  if (token && tokenExpiry) {
    // Update the active session timestamp
    localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, Date.now().toString());
    
    // Check if token is still valid
    return Date.now() < parseInt(tokenExpiry);
  }
  
  return false;
};

// Function to log in user (store credentials)
export const loginUser = (token, expiryInMinutes = 60, userInfo = {}) => {
  const expiryTime = Date.now() + (expiryInMinutes * 60 * 1000);
  
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
  localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, Date.now().toString());
  
  if (userInfo && Object.keys(userInfo).length > 0) {
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
  }
  
  // Notify other tabs about the login
  localStorage.setItem(STORAGE_KEYS.SESSION_REFRESH, Date.now().toString());
};

// Function to log out user (clear credentials)
export const logoutUser = () => {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
};

// Function to check if the session is active in other tabs
export const isSessionActiveInOtherTabs = () => {
  const lastActive = localStorage.getItem(STORAGE_KEYS.ACTIVE_SESSION);
  if (!lastActive) return false;
  
  // Consider a session active if there was activity in the last minute
  const activeThreshold = 60000; // 1 minute in milliseconds
  return (Date.now() - parseInt(lastActive)) < activeThreshold;
};

// ProtectedRoute wrapper component
export const ProtectedRoute = ({ children }) => {
  // On component mount, check authentication
  useEffect(() => {
    // Update active session timestamp
    if (isAuthenticated()) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, Date.now().toString());
    }
  }, []);
  
  if (!isAuthenticated()) {
    // Clear any expired tokens
    logoutUser();
    
    // Redirect to landing page instead of login
    return <Navigate to="/" replace />;
  }

  return children;
};

// AuthWrapper component to apply token checking across protected routes
export const AuthWrapper = ({ children }) => {
  useAuthCheck();
  
  // Update active session timestamp periodically
  useEffect(() => {
    const updateActiveSession = () => {
      if (isAuthenticated()) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, Date.now().toString());
      }
    };
    
    // Update active timestamp every 30 seconds
    const intervalId = setInterval(updateActiveSession, 30000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  return <>{children}</>;
};